import {useState, useEffect} from 'react';
import {Platform, View, Image} from 'react-native';
import type {NavigationProp} from '@react-navigation/native';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import moment from 'moment';
import {
  EliteBuyerTierIcon,
  MemberTierIcon,
  SmartShopperTierIcon,
  TrailblazerTierIcon,
  UserRewardsTierIcon,
} from 'src/components/img/svg/rewards';
import {
  getMemberTierInfo,
  MemberCodes,
  getCurrentUserPointsName,
  getRewardProgramForUserTier,
  getNextTierInfo,
} from 'src/components/elements/rewards/rewardsUtil';
import AVText from 'src/components/elements/AVText';
import PayEarnPoints from 'src/components/img/svg/rewards/PayEarnPoints';
import EllipseCircle from 'src/components/img/svg/EllipseCircle';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import RewardDetails from 'src/components/elements/rewards/RewardDetails';
import AccountStore from 'src/stores/AccountStore';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import {rewardStatusConfig} from 'src/components/elements/rewards/rewardsstatus/rewardStatusHelper';
import Util, {getArticle} from 'src/Util';
import ExclusiveRewards from 'src/components/img/svg/rewards/ExclusiveRewards';
import {getDescriber as getDescriberCommon} from 'src/components/screens/descriptor/DescriptorType';
import Settings from 'src/Settings';
import {getDescriber} from './decriptor/DescriptorType';

const styles = getDescriber().getStyleDescriptor();

interface RewardsStatusScreenProps {
  navigation: NavigationProp<Record<string, never>>;
}

const RewardsStatusScreen = (_props: RewardsStatusScreenProps) => {
  const {rewardTier, rewardStatuses} = AccountStore.getRewardStatus() || {};
  const [showRewardDetail, setShowRewardDetail] = useState(false);
  const MAX_TIER_LEVEL = 4;

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'RewardStatusScreen');
    return () => {
      FirebaseAnalytic.trackEvent('componentWillUnmount', 'RewardStatusScreen');
    };
  }, []);

  const statusTierDetailsdesc = {
    1: Localized.Labels.member_status_details,
    2: Localized.Labels.smartshopper_status_details,
    3: Localized.Labels.trail_status_details,
    4: Localized.Labels.elite_status_details,
  };

  const renderRewardDetails = () => {
    return (
      <RewardDetails
        statusTiers={rewardStatuses}
        statusTierDetailsdesc={statusTierDetailsdesc}
        hideModal={hideRewardDetailModal}
      />
    );
  };

  const hideRewardDetailModal = () => {
    setShowRewardDetail(false);
  };

  const getTierImage = () => {
    const userMemberTierObj = getMemberTierInfo(
      rewardTier?.tierName,
      rewardStatuses,
    );
    switch (userMemberTierObj.tierName) {
      case MemberCodes.MEMBER:
        return <MemberTierIcon />;
      case MemberCodes.SMART_SHOPPER:
        return <SmartShopperTierIcon />;
      case MemberCodes.ELITE_BUYER:
        return <EliteBuyerTierIcon />;
      case MemberCodes.TRAIL_BLAIZER:
        return <TrailblazerTierIcon />;
    }
  };

  const getUserPointsName = () => {
    return getCurrentUserPointsName(rewardTier);
  };

  const getNextTier = () => {
    return getMaxPointsForTier() - rewardTier?.currentYearPoints;
  };

  const getMaxPointsForTier = () => {
    const rewardProgram = getNextTierInfo(rewardTier, rewardStatuses);
    return rewardProgram.pointsThreshold ?? 0;
  };

  const getPointsRateForTier = () => {
    const rewardProgram = getRewardProgramForUserTier(
      rewardTier,
      rewardStatuses,
    );
    return rewardProgram.pointsRate;
  };

  const getCurrentTierLevel = () => {
    const rewardProgram = getRewardProgramForUserTier(
      rewardTier,
      rewardStatuses,
    );
    return rewardProgram.tierLevel;
  };

  const getPointsIndicator = () => {
    const maxPointsForTier = getMaxPointsForTier();
    const userPoints =
      rewardTier?.currentYearPoints == 0 ? 0.1 : rewardTier?.currentYearPoints;
    let availableFlex: number;
    if (userPoints > 0 && maxPointsForTier > 0) {
      availableFlex = userPoints / maxPointsForTier;
      return availableFlex;
    } else {
      availableFlex = 0;
      return availableFlex;
    }
  };

  const getUserPointsMultiplier = () => {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'space-around',
          flexDirection: 'row',
          marginTop: 24,
          marginBottom: 32,
        }}
      >
        <View
          style={{
            width: '40%',
            marginHorizontal: Styles.Spacing.m2,
          }}
        >
          <View style={{alignSelf: 'center'}}>
            <PayEarnPoints
              color={getDescriberCommon().getOneTimePayEarnColor()}
            />
          </View>
          <AVText
            style={[
              styles.exclRewards,
              {fontFamily: rewardStatusConfig.textFontFamily},
            ]}
            accessibilityLabel={`$1= ${getPointsRateForTier()} points`}
            aria-label={`$1= ${getPointsRateForTier()} points`}
          >
            {`$1= ${getPointsRateForTier()} points`}
          </AVText>
          <AVText
            style={[
              styles.pointsMultiplier,
              {fontFamily: rewardStatusConfig.textFontFamily},
            ]}
            accessibilityLabel={`As a ${getUserPointsName()}, you earn ${getPointsRateForTier()} points for each $1 you spend`}
            aria-label={`As a ${getUserPointsName()}, you earn ${getPointsRateForTier()} points for each $1 you spend`}
          >
            {Localized.Labels.formatString(
              Localized.Labels.earn_points_desc,
              getUserPointsName(),
              getPointsRateForTier(),
              getArticle(getUserPointsName()),
            )}
          </AVText>
        </View>

        <View style={{width: '40%', marginHorizontal: Styles.Spacing.m2}}>
          <View style={{alignSelf: 'center'}}>
            <ExclusiveRewards
              color={getDescriberCommon().getOneTimePayEarnColor()}
            />
          </View>
          <AVText
            style={[
              styles.exclRewards,
              {fontFamily: rewardStatusConfig.textFontFamily},
            ]}
            accessibilityLabel={Localized.Labels.exclusive_rewards}
            aria-label={`${Localized.Labels.exclusive_rewards}, text`}
          >
            {Localized.Labels.exclusive_rewards}
          </AVText>
          <AVText
            style={[
              styles.pointsMultiplier,
              {fontFamily: rewardStatusConfig.textFontFamily},
            ]}
            accessibilityLabel={Localized.Labels.exclusive_rew_desc}
            aria-label={`${Localized.Labels.exclusive_rew_desc}, text`}
          >
            {Localized.Labels.exclusive_rew_desc}
          </AVText>
        </View>
      </View>
    );
  };

  const getUserPointsAway = () => {
    return (
      <View
        style={{
          alignSelf: 'flex-start',
          marginLeft: Styles.Spacing.m2,
        }}
      >
        <AVText
          style={[
            styles.pointsAway,
            {fontFamily: rewardStatusConfig.textFontFamily},
          ]}
          accessibilityLabel={Localized.Labels.redeem_points_for_rewards}
          aria-label={`${Localized.Labels.redeem_points_for_rewards}, text`}
        >
          {Localized.Labels.formatString(
            Localized.Labels.you_are_points_away,
            getNextTier(),
          )}
        </AVText>
      </View>
    );
  };

  const getSeeMoreDetails = () => {
    return (
      <RoundedAppButton
        accessibilityLabelValue={getDescriberCommon().seeMoreDetailtxt()}
        aria-label={getDescriberCommon().seeMoreDetailtxt()}
        titleText={getDescriberCommon().seeMoreDetailtxt()}
        buttonType={RoundedAppButtonType.Outline}
        textStyle={[
          {
            fontFamily: rewardStatusConfig.textFontFamily,
            fontWeight: '700',
            fontSize: Styles.Fonts.f1,
            textAlign: 'center',
          },
          !Settings.isAramark() && {flex: 1, textTransform: 'capitalize'},
        ]}
        buttonViewStyle={{
          height: 42,
          backgroundColor: Styles.white,
        }}
        buttonContainerStyle={{
          marginTop: 20,
          alignSelf: 'center',
          paddingBottom: Styles.Spacing.m3,
        }}
        onPress={() => setShowRewardDetail(true)}
      />
    );
  };

  const getDisclaimer = () => {
    return (
      <View
        style={{
          alignSelf: 'flex-start',
          marginLeft: Styles.Spacing.m2,
        }}
      >
        <AVText
          style={[
            styles.discalimerText,
            {fontFamily: rewardStatusConfig.textFontFamily},
          ]}
          accessibilityLabel={Localized.Labels.make_one_purchase}
          aria-label={`${Localized.Labels.make_one_purchase}, text`}
        >
          {Localized.Labels.make_one_purchase}
        </AVText>
      </View>
    );
  };

  const getUserTierIconAndName = () => {
    return (
      <View style={styles.tierIconAndName}>
        <View
          style={{
            marginLeft: Styles.Spacing.m2,
          }}
        >
          <UserRewardsTierIcon
            tierName={getNextTierInfo(rewardTier, rewardStatuses).nextTierName}
            size={
              Platform.OS === 'web'
                ? Styles.Heights.webRewardImageHeight
                : Styles.Spacing.m4
            }
          />
        </View>
        <View
          style={{
            justifyContent: 'center',
          }}
        >
          <AVText
            style={[
              styles.userTierName,
              {fontFamily: rewardStatusConfig.textFontFamily},
            ]}
            accessibilityLabel={Localized.Labels.redeem_points_for_rewards}
            aria-label={`${Localized.Labels.redeem_points_for_rewards}, text`}
          >
            {getNextTierInfo(rewardTier, rewardStatuses).nextTierName}
          </AVText>
        </View>
      </View>
    );
  };

  const getUserPointsPerYear = () => {
    return (
      <AVText
        accessibilityLabel={`${rewardTier?.currentYearPoints}`}
        aria-label={`${rewardTier?.currentYearPoints}, text`}
        style={styles.userPointsValueTitle}
      >
        {Localized.Labels.formatString(
          Localized.Labels.points_number,
          rewardTier?.currentYearPoints,
        )}
      </AVText>
    );
  };

  const getExpiryDate = () => {
    const lastDayOfYear = moment().endOf('year').format('MM/DD/YYYY');
    return lastDayOfYear;
  };

  const getExpiryDateComponent = () => {
    return (
      <AVText
        accessibilityLabel={`Status expires ${getExpiryDate()}`}
        aria-label={`Status expires ${getExpiryDate()}, text`}
        style={styles.statusExpires}
      >
        {Localized.Labels.formatString(
          rewardStatusConfig.statusExpires,
          getExpiryDate(),
        )}
      </AVText>
    );
  };

  const getUserPointsTitle = () => {
    return (
      <AVText
        accessibilityLabel={`${getUserPointsName()}`}
        aria-label={`${getUserPointsName()}, text`}
        style={[
          styles.userPointsNameTitle,
          {fontFamily: rewardStatusConfig.textFontFamily},
        ]}
      >
        {getUserPointsName()}
      </AVText>
    );
  };

  const getUserPointsComponent = () => {
    return (
      <View
        style={{
          alignSelf: 'flex-end',
        }}
      >
        <AVText
          style={[
            styles.howManyPoints,
            {fontFamily: rewardStatusConfig.textFontFamily},
          ]}
          accessibilityLabel={Localized.Labels.points_number_away}
        >
          {Localized.Labels.formatString(
            Localized.Labels.points_number_away,
            Util.numberWithCommas(rewardTier?.currentYearPoints),
            Util.numberWithCommas(getMaxPointsForTier()),
          )}
        </AVText>
      </View>
    );
  };

  const getBgColor = () => {
    if (getPointsIndicator() == 0.001) {
      return Styles.Colors.PayNew.neutralHuesBase09;
    } else {
      return rewardStatusConfig.pointsIndicatorFillColor;
    }
  };

  const getZeroMargin = () => {
    if (rewardTier?.currentYearPoints == 0) {
      return -5;
    } else {
      return null;
    }
  };

  const getPointsIndicatorComponent = () => {
    const webImage = Settings.isRefiveAnd365PayOnly()
      ? require('src/components/img/EllipseCirclePay.png')
      : require('src/components/img/EllipseCircleCP.png');
    return (
      <View style={[styles.payGraph]} accessible={true} role="img" key={-1}>
        <View style={styles.line}>
          <View style={[styles.rewardOption, styles.flex1]}>
            <View
              style={[
                styles.payAvailableOption,
                styles.firstSection,
                {
                  flex: Styles.Spacing.m1,
                },
                {backgroundColor: getBgColor()},
              ]}
            />
          </View>
        </View>
        <View style={[styles.line, styles.flex15]}>
          <View
            style={[
              styles.rewardOption,
              {
                flex: 1,
              },
            ]}
          >
            <View
              style={[
                styles.payAvailableOption,
                {
                  flex: getPointsIndicator(),
                },
                {
                  backgroundColor: getBgColor(),
                  marginLeft: getZeroMargin(),
                },
              ]}
            >
              <View
                style={[
                  styles.roundIndicatorStyle,
                  styles.circleIndicatorShadow,
                ]}
              >
                {Platform.OS === 'web' ? (
                  <Image
                    source={webImage}
                    style={styles.pngFallback}
                    resizeMode="contain"
                  />
                ) : (
                  <EllipseCircle
                    firstColor={rewardStatusConfig.indicatorGradient1}
                    secondColor={rewardStatusConfig.indicatorGradient2}
                  />
                )}
              </View>
            </View>

            <View
              style={[
                styles.payUnavailableOption,
                {
                  flex: 1 - getPointsIndicator(),
                  paddingLeft: 1,
                  marginLeft: 1,
                },
              ]}
            />
          </View>
        </View>
        <View style={styles.line}>
          <View style={[styles.rewardOption, styles.flex1]}>
            <View
              style={[
                styles.payUnavailableOption,
                styles.lastSection,
                {
                  flex: Styles.Spacing.m1,
                },
              ]}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {getTierImage()}
      {getUserPointsTitle()}
      {getUserPointsPerYear()}
      {getExpiryDateComponent()}
      {getUserPointsMultiplier()}
      <View style={styles.divider} />

      {getCurrentTierLevel() < MAX_TIER_LEVEL && (
        <>
          {getUserPointsAway()}

          {getUserTierIconAndName()}
          {getUserPointsComponent()}
          {getPointsIndicatorComponent()}
        </>
      )}

      {getDisclaimer()}
      {getSeeMoreDetails()}
      {showRewardDetail && renderRewardDetails()}
    </View>
  );
};

export default RewardsStatusScreen;
